import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { AutocompletePropsSizeOverrides } from "@mui/material/Autocomplete";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { SxProps } from "@mui/system";
import { OverridableStringUnion } from "@mui/types";
import SearchIcon from "@mui/icons-material/Search";

const getLabel = (val: any): string =>
  typeof val === "string" ? val : val?.title || val?.label || "";

interface ISelectProps {
  id?: string;
  options: readonly any[];
  sx?: SxProps;
  label?: string;
  placeholder?: string;
  name?: string;
  value?: any | null;
  margin?: "dense" | "normal" | "none" | undefined;
  size?: OverridableStringUnion<"small" | "medium", AutocompletePropsSizeOverrides> | undefined;
  onSelect?: React.ReactEventHandler<HTMLDivElement> | undefined;
  onChange?: (event: any, newValue: any | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  noOptionsText?: React.ReactNode;
  disablePortal?: boolean;
  autoFocus?: boolean;
}

const AutocompleteSelect: React.FC<ISelectProps> = ({
  id = "Autocomplete",
  options,
  sx,
  label,
  placeholder,
  multiple = false,
  name = "autocomplete",
  value,
  margin = "normal",
  size = "medium",
  onChange,
  onSelect,
  disabled = false,
  noOptionsText = "No options",
  disablePortal = false,
  autoFocus = false,
}) => {
  const [inputValue, setInputValue] = React.useState(
    () => (multiple || !value) ? "" : getLabel(value)
  );

  React.useEffect(() => {
    setInputValue((multiple || !value) ? "" : getLabel(value));
  }, [value, multiple]);

  const componentsProps: SxProps = {
    paper: {
      sx: {
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        "& .MuiAutocomplete-noOptions": {
          color: "var(--foreground)",
        },
      },
    },
    popper: {
      sx: {
        zIndex: 1400,
        "& .MuiAutocomplete-listbox": {
          backgroundColor: "var(--background)",
          color: "var(--foreground)",
        },
        "& .MuiAutocomplete-option": {
          color: "var(--foreground)",
          "&:hover": {
            backgroundColor: "var(--accent)",
          },
          '&[aria-selected="true"]': {
            backgroundColor: "var(--accent)",
            color: "var(--accent-foreground)",
          },
        },
        "& .MuiAutocomplete-noOptions": {
          color: "var(--foreground)",
        },
      },
    },
    clearIndicator: {
      sx: {
        color: "var(--muted-foreground)",
        padding: "1px",
        marginRight: "1px",
        "&:hover": {
          color: "var(--foreground)",
          backgroundColor: "var(--secondary)",
          borderRadius: "50%",
        },
      },
    },
    popupIndicator: {
      color: "inherit",
      sx: {
        transform: "none",
      },
    },
  };
  return (
    <Autocomplete
      disabled={disabled}
      multiple={multiple}
      disablePortal={disablePortal}
      sx={{
        ...sx,
        "& .MuiAutocomplete-endAdornment": {
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          right: 8,
          display: "flex",
          flexDirection: "row-reverse",
        },
        "& .MuiChip-root": {
          backgroundColor: "var(--accent)",
          color: "var(--accent-foreground)",
        },
        "& .MuiChip-deleteIcon": {
          color: "var(--muted-foreground)",
          "&:hover": {
            color: "var(--foreground)",
          },
        },
      }}
      fullWidth
      autoHighlight
      clearOnEscape
      // disableCloseOnSelect // the popup won't close when a value is selected.
      id={`${id}`}
      value={multiple ? (Array.isArray(value) ? value : []) : (value ?? null)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      popupIcon={<SearchIcon />}
      forcePopupIcon={!inputValue}
      disableClearable={!inputValue}
      onChange={onChange}
      onSelect={onSelect}
      size={size}
      options={options}
      getOptionLabel={(option) => option.title}
      componentsProps={componentsProps}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          name={name}
          {...params}
          label={label}
          placeholder={placeholder}
          margin="none"
          autoFocus={autoFocus}
          sx={{
            "& .MuiInputBase-input": { color: "var(--foreground)", padding: "0" },
            "& .MuiInputLabel-root": { color: "var(--muted-foreground)" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--input)", borderRadius: "var(--radius)" },
            "& .MuiInputBase-root": { margin: 0, padding: "8px 8px", minHeight: "36px", borderRadius: "var(--radius)" },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ring)", borderWidth: "1px" },
          }}
        />
      )}
      renderOption={(props, option, { inputValue }) => {
        const optionText =
          typeof option === "string"
            ? option
            : option?.title || option?.label || "";
        const safeSource = optionText || "";
        const safeQuery = inputValue || "";
        const matches = match(safeSource, safeQuery, { insideWords: true });
        const parts = parse(safeSource, matches);
        return (
          <li {...props}>
            <div>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{
                    fontWeight: part.highlight ? 700 : 400,
                    color: "var(--foreground)",
                  }}>
                  {part.text}
                </span>
              ))}
            </div>
          </li>
        );
      }}
    />
  );
};
export default AutocompleteSelect;
